import { FileSpreadsheet, FileText, X } from 'lucide-react';

export type ExportFormat = 'xlsx' | 'csv';

/**
 * "Excel or CSV?" — asked once, just before a download starts.
 *
 * Every export on the platform can produce either, and which one you want depends entirely on what
 * you are about to do with it: open it and read it (Excel), or feed it to another system (CSV).
 * Guessing was wrong either way — a CSV opened in Excel turns a 14-digit AWB into `6.0051E+13` and
 * quietly drops its last digits, while a workbook handed to a script that expects text is useless.
 *
 * Deliberately two big buttons rather than a dropdown plus a confirm: one click still starts the
 * download, so nobody has to learn a new step.
 */
export function ExportFormatDialog({
  open,
  title = 'Download',
  subtitle,
  busy,
  onPick,
  onClose,
}: {
  open: boolean;
  title?: string;
  subtitle?: string;
  busy?: boolean;
  onPick: (format: ExportFormat) => void;
  onClose: () => void;
}) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/40 p-4" onClick={busy ? undefined : onClose}>
      <div className="bg-white dark:bg-slate-900 rounded-xl shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-5 pt-4 pb-2">
          <div>
            <h3 className="font-semibold text-slate-800 dark:text-slate-100">{title}</h3>
            <p className="text-xs text-slate-500 mt-0.5">{subtitle ?? 'Which format would you like?'}</p>
          </div>
          <button onClick={onClose} disabled={busy} className="text-slate-400 hover:text-slate-600 disabled:opacity-40">
            <X size={18} />
          </button>
        </div>

        <div className="px-5 pb-5 pt-2 grid grid-cols-2 gap-3">
          <button
            onClick={() => onPick('xlsx')}
            disabled={busy}
            className="border border-emerald-200 bg-emerald-50 hover:bg-emerald-100 rounded-lg p-4 text-left disabled:opacity-50"
          >
            <FileSpreadsheet size={20} className="text-emerald-700" />
            <div className="font-medium text-sm text-emerald-900 mt-2">Excel (.xlsx)</div>
            <div className="text-[11px] text-emerald-800/80 mt-1 leading-snug">
              Best for opening and reading. Long AWB and LR numbers keep every digit.
            </div>
          </button>

          <button
            onClick={() => onPick('csv')}
            disabled={busy}
            className="border border-slate-200 bg-slate-50 hover:bg-slate-100 rounded-lg p-4 text-left disabled:opacity-50"
          >
            <FileText size={20} className="text-slate-600" />
            <div className="font-medium text-sm text-slate-800 mt-2">CSV (.csv)</div>
            <div className="text-[11px] text-slate-600 mt-1 leading-snug">
              Best for uploading into another system. Excel may shorten long numbers.
            </div>
          </button>
        </div>

        {busy && <div className="px-5 pb-4 text-xs text-slate-500">Preparing the file…</div>}
      </div>
    </div>
  );
}
